import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Calendar } from "lucide-react";
import AppButton from "@/components/AppButtons";
import { MapleMark } from "@/components/MapleMark";
import { SITE } from "@/config/site";
import { scrollToId } from "@/lib/scroll";
import { EASE } from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1766345907386-3eff10cb9142?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBuYXR1cmUlMjBwaW5lJTIwZm9yZXN0JTIwZm9nfGVufDB8fHx8MTc4OTE3ODU4OHww&ixlib=rb-4.1.0&q=85";

const LINES = [
  <>Bookkeeping &amp;</>,
  <>payroll that keeps</>,
  <>
    your business <span className="text-accent">moving.</span>
  </>,
];

export default function Hero() {
  const reduced = useReducedMotion();
  const imgWrap = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgWrap, offset: ["start start", "end start"] });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  return (
    <section className="ledger-lines relative overflow-hidden">
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-brand/5 blur-3xl"
        aria-hidden="true"
      />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 pb-24 pt-32 sm:pt-36 lg:min-h-[92vh] lg:grid-cols-12 lg:px-8 lg:pb-28 lg:pt-40">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={reduced ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand"
            data-testid="hero-eyebrow"
          >
            <MapleMark className="h-4 w-4 text-accent" />
            Newmarket, Ontario — serving Canadian businesses
          </motion.p>

          <h1
            className="mt-6 font-cabinet text-5xl font-extrabold leading-[0.98] tracking-tighter text-ink sm:text-6xl lg:text-7xl"
            data-testid="hero-heading"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={reduced ? false : { y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={reduced ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: EASE }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600"
            data-testid="hero-subcopy"
          >
            Postings Inc. provides professional bookkeeping, payroll and business financial
            support for Canadian businesses — helping owners stay organized, compliant and
            informed without getting buried in paperwork.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={reduced ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.68, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-get-started"
              className="inline-flex h-12 items-center rounded-full bg-brand px-7 text-sm font-bold text-white shadow-ambient transition-all duration-200 hover:-translate-y-px hover:bg-[rgb(0_40_140)]"
            >
              Get Started
            </Link>
            <button
              onClick={() => scrollToId("services")}
              data-testid="hero-cta-services"
              className="inline-flex h-12 items-center gap-2 rounded-full border border-ink/15 bg-white/70 px-7 text-sm font-bold text-ink transition-colors hover:border-brand hover:text-brand"
            >
              Explore Our Services
              <ArrowDown className="h-4 w-4" aria-hidden="true" />
            </button>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-booking"
              className="inline-flex h-12 items-center gap-2 rounded-full px-4 text-sm font-bold text-slate-600 transition-colors hover:text-brand"
            >
              <Calendar className="h-4 w-4 text-accent" aria-hidden="true" />
              Book a Consultation
            </a>
          </motion.div>
        </div>

        <div className="relative mb-10 lg:col-span-5 lg:mb-0" ref={imgWrap}>
          <motion.div
            initial={reduced ? false : { opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
            className="relative overflow-hidden rounded-3xl shadow-ambient"
          >
            <motion.img
              style={reduced ? undefined : { y: parallaxY, scale: 1.12 }}
              src={HERO_IMG}
              alt="Misty evergreen forest — a nod to the Canadian landscape Postings calls home"
              className="h-[420px] w-full object-cover sm:h-[520px]"
              loading="eager"
              fetchPriority="high"
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-deep/25 via-transparent to-transparent"
              aria-hidden="true"
            />
          </motion.div>

          <motion.div
            initial={reduced ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="absolute -bottom-10 left-4 max-w-[320px] rounded-2xl border border-white/60 bg-white/85 p-5 shadow-hover backdrop-blur-xl sm:left-8"
            data-testid="hero-client-card"
          >
            <p className="text-sm font-bold text-ink">Already a Postings client?</p>
            <p className="mt-0.5 text-xs text-slate-500">Jump straight into your tools.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <AppButton app={SITE.apps.clientManager} variant="solid" size="sm" testid="hero-app-client-manager" />
              <AppButton app={SITE.apps.postWage} variant="ink" size="sm" testid="hero-app-postwage" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
